import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setSection, updateSection } from "../../../assignmentSlice";
import { getGradeAPI } from "../../../assignmentAPI";
import { getStartingIndex } from "../../../../../utils/resumeUtils";

import UserInputBar from "../../widgets/UserInputBar";
import { typeInfo } from "../../../../../constants/typeInfo";
import CommonPage from "../../common/CommonPage";

const UnderstandingTestPage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const { section } = useSelector((state) => state.assignment);

  const [pageList, setPageList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [userInput, setUserInput] = useState("");
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const currentPage = pageList[currentIndex] || {
    type: "",
  };

  useEffect(() => {
    if (pageList.length > 0) return;

    if (!isPreview && section.answer.length > 0) {
      setPageList(section.answer);
      setCurrentIndex(getStartingIndex(section.resumeIndex));
      return;
    }
    setPageList(
      section.question.data.map((question) => {
        return { ...question, isSubmitted: false };
      })
    );
    setCurrentIndex(0);
  }, [section]);

  useEffect(() => {
    if (currentPage.isSubmitted) {
      if (currentPage.isCorrect) {
        setUserInput(currentPage.input);
      } else {
        setUserInput(currentPage.answer);
      }
    } else {
      setUserInput("");
    }
  }, [currentIndex]);

  const handleChange = (e) => {
    setUserInput(e.target.value);
  };

  const handleSetAnswer = () => {
    const newPageList = pageList.map((page, pageIndex) => {
      if (pageIndex === currentIndex) {
        return {
          ...page,
          input: userInput,
        };
      }
      return page;
    });
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
  };

  const handleNextPage = () => {
    if (currentIndex < pageList.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      let isAllAnswered = true;

      pageList.forEach((page, index) => {
        if (!page.isSubmitted) {
          isAllAnswered = false;
        }
      });

      if (isAllAnswered) {
        if (!isPreview && !isReview) {
          dispatch(
            updateSection({
              sectionId: section.id,
              data: { is_finished: true, answer: pageList },
            })
          );
        }
        alert("학습이 완료되었습니다.");
        setCurrentStep(2);
      } else {
        alert("문제를 덜 풀었습니다.");
      }
    }
  };

  const handlePreviousPage = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = async () => {
    if (currentPage.isSubmitted) return;
    if (userInput === "") {
      return alert("답을 입력해주세요.");
    }

    const grade = await getGradeAPI({
      data: {
        question: currentPage.question,
        input: userInput,
      },
      type: "understanding",
    });

    setOverlayVisible(true);
    setIsCorrect(grade.is_correct === 1);

    const newPageList = pageList.map((page, pageIndex) => {
      if (pageIndex === currentIndex) {
        return {
          ...page,
          input: userInput,
          isCorrect: grade.is_correct === 1,
          isSubmitted: true,
        };
      }
      return page;
    });
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));

    setTimeout(() => {
      setOverlayVisible(false);
      if (grade.is_correct === 0) {
        setUserInput(currentPage.answer);
      }
    }, 600);
  };

  return (
    <CommonPage
      currentIndex={currentIndex}
      handleNextPage={handleNextPage}
      handlePreviousPage={handlePreviousPage}
      totalNo={pageList.length}
      currentPage={currentPage}
      paragraph={section?.question.full_text}
      accentColor={typeInfo.comprehensive.color}
      overlayVisible={overlayVisible}
      isCorrect={isCorrect}
      handleSubmit={handleSubmit}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "50px",
          width: Math.min(window.innerWidth, window.screen.width) <= 768 ? `${Math.min(window.innerWidth, window.screen.width) - 56}px` : "90%", // [여백 수술] 화면 기준 폭
          maxWidth: "680px",
          margin: "0 auto", // [중앙 정렬] 지문+문제 열이 왼쪽에 붙던 것 — 남는 폭을 좌우로 균등 분배
          boxSizing: "border-box",
        }}
      >
        {/* [내용이해 PC 리디자인] Q칩 + 카운터 + 진행바 — 모고와 동일 문법, 내용이해 토큰 */}
        <div style={{ width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
            <span style={{ backgroundColor: "#0E141B", color: "#FFF", borderRadius: "8px", fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "12px" : "13px", fontWeight: 800, padding: "2px 10px" }}>
              Q{currentIndex + 1}
            </span>
            <span style={{ marginLeft: "auto", fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "12px" : "13px", color: "#7A8490", fontWeight: 700 }}>
              {currentIndex + 1} / {pageList.length}
            </span>
          </div>
          <div style={{ height: "4px", borderRadius: "999px", backgroundColor: "#EDF2F1", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${((currentIndex + 1) / Math.max(pageList.length, 1)) * 100}%`, backgroundColor: typeInfo.comprehensive.color, borderRadius: "999px", transition: "width .25s" }} />
          </div>
        </div>
        <div style={{ fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "14px" : "20px", fontWeight: 700, textAlign: "center" }}>본문 내용에 따라, 다음 질문에 영어로 답하세요.</div>
        <div
          style={{
            fontSize: Math.min(window.innerWidth, window.screen.width) <= 768 ? "19px" : "28px", // [타이포 통일] 문장테스트와 동일
            textAlign: "center",
            lineHeight: "1.5",
          }}
        >
          {currentPage.question}
        </div>
        <div style={{ width: "80%", position: "relative", marginBottom: Math.min(window.innerWidth, window.screen.width) <= 768 ? "48px" : 0 }}>
          <UserInputBar
            userInput={userInput}
            handleChange={handleChange}
            type={"comprehensive"}
            handleSetAnswer={handleSubmit}
            onlyUnderline={true}
            isSubmitted={currentPage.isSubmitted}
          />
        </div>
      </div>
    </CommonPage>
  );
};

export default UnderstandingTestPage;
